import React, { useState, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';

const MicDropStatus = ({
  isConnected,
  wordChangeCount,
  fromRecorder,
  isSpeaking,
}) => {
  const [amplitude, setAmplitude] = useState(10);
  const [lastChangeTime, setLastChangeTime] = useState(Date.now());

  // Update amplitude and last change time when wordChangeCount changes
  useEffect(() => {
    setAmplitude(40); // Increase amplitude on new word recognition
    setLastChangeTime(Date.now());
  }, [wordChangeCount]);

  // Gradually decrease amplitude over time if no new words are recognized
  useEffect(() => {
    const interval = setInterval(() => {
      const timeElapsed = (Date.now() - lastChangeTime) / 1000; // time elapsed in seconds
      const newAmplitude = Math.max(10, 40 - timeElapsed * 10); // Decrease amplitude over time
      setAmplitude(newAmplitude);
    }, 100);

    return () => clearInterval(interval);
  }, [lastChangeTime]);

  // Define the animation spring for the word change effect
  const springProps = useSpring({
    transform: `scale(${isConnected ? 1.2 : 1})`,
    config: { tension: 200, friction: 10 },
  });

  const getCSSVariable = (variableName) => {
    return getComputedStyle(document.documentElement).getPropertyValue(
      variableName,
    );
  };

  // Generate the SVG dynamically based on the amplitude
  const generateWaveEffect = () => {
    const bars = [];
    const barWidth = 8;
    const totalBars = 5;
    let frequency = (2 * Math.PI) / totalBars; // Frequency to fit one full sine wave over the total number of bars

    for (let i = 0; i < totalBars; i++) {
      let height =
        10 +
        amplitude *
          Math.abs(Math.sin(frequency * 0.5 * i + wordChangeCount / 3));
      if (!isConnected) height = 10; // Reset height if disconnected

      const y = (50 - height) / 2;
      const x = 25 - (totalBars / 2 - i) * (barWidth + 4);

      // Access the primary and danger colors from CSS variables
      const primaryColor =
        fromRecorder && isSpeaking
          ? getCSSVariable('--white').trim()
          : fromRecorder && !isSpeaking
            ? getCSSVariable('--danger').trim()
            : getCSSVariable('--primary').trim();
      const dangerColor = getCSSVariable('--danger').trim();

      bars.push(
        <rect
          key={i}
          x={x}
          y={y}
          rx={4} // Rounded corners
          width={barWidth}
          height={height}
          fill={isConnected ? primaryColor : dangerColor}
        />,
      );
    }
    return bars;
  };

  return (
    <animated.svg
      width="60"
      height={fromRecorder ? '27' : '50'}
      style={springProps}
      viewBox="0 0 50 50"
    >
      {generateWaveEffect()}
    </animated.svg>
  );
};

export default MicDropStatus;
